import type { Attribute, BooleanAttribute } from 'domain/experiment/attribute/attribute-model';
import { getAttribute } from 'domain/experiment/attribute/attribute-model-utils';
import { KnownAttributes } from 'domain/experiment/attribute/known-attributes';

export type RunAttributeDiagnosticsRow = {
  key: KnownAttributes;
  label: string;
  value: number;
};

export type RunAttributeDiagnosticsWarningKind =
  | 'attribute-cap-applied'
  | 'float-series-attribute-cap-applied'
  | 'dropped-float-series-detected';

export type RunAttributeDiagnosticsWarning = {
  kind: RunAttributeDiagnosticsWarningKind;
  message: string;
};

const RUN_ATTRIBUTE_DIAGNOSTICS_WARNING_MESSAGES: Record<
  RunAttributeDiagnosticsWarningKind,
  string
> = {
  'attribute-cap-applied': 'Attribute cap applied',
  'float-series-attribute-cap-applied': 'Float series attribute cap applied',
  'dropped-float-series-detected': 'Dropped float series detected',
};

const COUNT_DEFINITIONS: Array<{ key: KnownAttributes; label: string }> = [
  { key: KnownAttributes.DiagnosticsAttributesTotalCount, label: 'Total attributes' },
  {
    key: KnownAttributes.DiagnosticsAttributesTotalSeriesDatapoints,
    label: 'Logged series datapoints',
  },
  { key: KnownAttributes.DiagnosticsAttributesBoolCount, label: 'Booleans' },
  { key: KnownAttributes.DiagnosticsAttributesIntCount, label: 'Integers' },
  { key: KnownAttributes.DiagnosticsAttributesFloatCount, label: 'Floats' },
  { key: KnownAttributes.DiagnosticsAttributesStringCount, label: 'Strings' },
  { key: KnownAttributes.DiagnosticsAttributesStringSetCount, label: 'String sets' },
  { key: KnownAttributes.DiagnosticsAttributesFloatSeriesCount, label: 'Float series' },
  { key: KnownAttributes.DiagnosticsDroppedFloatSeriesCount, label: 'Dropped float series' },
  { key: KnownAttributes.DiagnosticsAttributesStringSeriesCount, label: 'String series' },
  { key: KnownAttributes.DiagnosticsAttributesFileRefCount, label: 'Files' },
  { key: KnownAttributes.DiagnosticsAttributesFileRefSeriesCount, label: 'File series' },
  { key: KnownAttributes.DiagnosticsAttributesHistogramCount, label: 'Histograms' },
  {
    key: KnownAttributes.DiagnosticsAttributesHistogramSeriesCount,
    label: 'Histogram series',
  },
];

const CAP_WARNING_DEFINITIONS: Array<{
  key: KnownAttributes;
  kind: RunAttributeDiagnosticsWarningKind;
}> = [
  {
    key: KnownAttributes.DiagnosticsAttributesLimitsTotalCountExceeded,
    kind: 'attribute-cap-applied',
  },
  {
    key: KnownAttributes.DiagnosticsAttributesLimitsFloatSeriesCountExceeded,
    kind: 'float-series-attribute-cap-applied',
  },
];

const COUNT_WARNING_DEFINITIONS: Array<{
  key: KnownAttributes;
  kind: RunAttributeDiagnosticsWarningKind;
}> = [
  {
    key: KnownAttributes.DiagnosticsDroppedFloatSeriesCount,
    kind: 'dropped-float-series-detected',
  },
];

export function getRunAttributeDiagnosticsRows(
  attributes: Attribute[],
): RunAttributeDiagnosticsRow[] {
  return COUNT_DEFINITIONS.flatMap(({ key, label }) => {
    const attribute = getAttribute(attributes, key, 'int');

    return attribute ? [{ key, label, value: attribute.value }] : [];
  });
}

export function getRunAttributeDiagnosticsWarnings(
  attributes: Attribute[],
): RunAttributeDiagnosticsWarning[] {
  const capWarnings = CAP_WARNING_DEFINITIONS.flatMap(({ key, kind }) => {
    const attribute = getAttribute(attributes, key, 'bool');

    return attribute?.value ? [createRunAttributeDiagnosticsWarning(kind)] : [];
  });
  const countWarnings = COUNT_WARNING_DEFINITIONS.flatMap(({ key, kind }) => {
    const attribute = getAttribute(attributes, key, 'int');

    return attribute && attribute.value > 0 ? [createRunAttributeDiagnosticsWarning(kind)] : [];
  });

  return [...capWarnings, ...countWarnings];
}

function createRunAttributeDiagnosticsWarning(
  kind: RunAttributeDiagnosticsWarningKind,
): RunAttributeDiagnosticsWarning {
  return {
    kind,
    message: RUN_ATTRIBUTE_DIAGNOSTICS_WARNING_MESSAGES[kind],
  };
}

export function hasRunAttributeDiagnostics(attributes: Attribute[]): boolean {
  return (
    getRunAttributeDiagnosticsRows(attributes).length > 0 ||
    CAP_WARNING_DEFINITIONS.some(({ key }) => Boolean(getAttribute(attributes, key, 'bool')))
  );
}

export function getRunAttributeDiagnosticsLimitAttribute(
  attributes: Attribute[],
  key: KnownAttributes,
): BooleanAttribute | undefined {
  return getAttribute(attributes, key, 'bool');
}
